import type { ContactRecipient } from "@/composables/useContactRecipientSearch";

export interface SendResult {
	emailsSent: number;
	smsSent: number;
	failedEmails: string[];
	failedPhoneNumbers: string[];
}

// The minimal shape resolveReportShareTarget reads off a Scribe analysis. Kept structural (rather
// than importing AnalysisResult) so the resolver stays a pure, easily-tested helper.
export interface ReportShareTargetSource {
	externalType?: string | null;
	externalId?: string | null;
	metadata?: {
		conversation?: { id?: string | null; name?: string | null; externalId?: string | null } | null;
		project?: { id?: string | null; name?: string | null; externalId?: string | null } | null;
	} | null;
	sessions?: Array<{ externalId?: string | null; externalType?: string | null; externalSource?: string | null }>;
}

export interface ReportShareTarget {
	externalType: string;
	externalId: string;
}

function firstNonEmptyString(...candidates: Array<string | null | undefined>): string {
	for (const candidate of candidates) {
		if (typeof candidate === "string" && candidate.trim().length > 0) return candidate.trim();
	}
	return "";
}

/**
 * Resolve the { externalType, externalId } the `/reports/share-notify` endpoint authorizes on.
 *
 * contact-api resolves the share target by id — it looks the id up as a Project, then a
 * Conversation, and 403s ("You do not have permission to share this report") if it is neither.
 * `externalType` is advisory (it can disagree with what `externalId` actually is, see
 * reports.ts), so the only thing that matters is sending a RESOLVABLE Contact-side id.
 *
 * `analysis.externalId` is the id Contact passed at report-creation time and is normally correct,
 * but an opened report can surface a partial/empty `externalId` (observed for reports rendered in
 * the embedded V2 iframe viewer). The opened-report Share path used to send it verbatim with no
 * fallback, so an empty value 403'd the report owner (LSP-16628). Resolve a usable id in order:
 * the analysis's own `externalId` → the Contact-side ids in its metadata → a contact session id
 * (when the analysis carries session rows at runtime) → the `fallback` enclosing entity id.
 *
 * The `fallback` is the project/conversation whose report list the viewer was opened from — the
 * exact final tier the report-LIST Share path already uses (EntityReports.shareReport's
 * `?? props.entity.id`). The opened-report viewer has the analysis but not that entity, so its
 * caller must pass it; with it, the opened path can always resolve a shareable id even when the
 * analysis carries none. The externalType-matching metadata id is preferred so a report that
 * carries both blocks authorizes at the intended boundary.
 */
export function resolveReportShareTarget(
	source: ReportShareTargetSource | null | undefined,
	fallback?: { externalId?: string | null; externalType?: string | null }
): ReportShareTarget {
	// Prefer the analysis's own type, then the enclosing entity's. externalId is resolved from the
	// fallback chain below. externalType is advisory to the backend; we keep it for diagnostics.
	const externalType = source?.externalType?.trim() || fallback?.externalType?.trim() || "";

	const conversationId = source?.metadata?.conversation?.id;
	const projectId = source?.metadata?.project?.id;
	const metadataIds = externalType === "project" ? [projectId, conversationId] : [conversationId, projectId];

	// Last-resort tier: some analyses carry session rows at runtime that AnalysisResult doesn't type
	// (the prior inline resolver reached them via a cast). Read them defensively when present.
	const contactSession = source?.sessions?.find(
		(session) => session?.externalType === "conversation" && session?.externalSource === "contact"
	);

	const externalId = firstNonEmptyString(source?.externalId, ...metadataIds, contactSession?.externalId, fallback?.externalId);

	return { externalType, externalId };
}

function pluralize(count: number, word: string): string {
	return `${count} ${word}${count === 1 ? "" : "s"}`;
}

function joinList(items: string[]): string {
	if (items.length <= 1) return items[0] ?? "";
	return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

export function summarizeSendOutcome(result: SendResult): { kind: "success" | "warning" | "error"; message: string } {
	const totalSent = result.emailsSent + result.smsSent;
	const totalFailed = result.failedEmails.length + result.failedPhoneNumbers.length;

	if (totalSent > 0 && totalFailed === 0) {
		const parts: string[] = [];
		if (result.emailsSent > 0) parts.push(pluralize(result.emailsSent, "email"));
		if (result.smsSent > 0) parts.push(pluralize(result.smsSent, "text message"));
		return { kind: "success", message: `Your report has been shared — we sent ${joinList(parts)}.` };
	}
	if (totalSent > 0 && totalFailed > 0) {
		return {
			kind: "warning",
			message: `Sent ${totalSent}, but couldn't reach ${totalFailed}. The failed recipients are still in the list — try sending again.`
		};
	}
	return { kind: "error", message: "We couldn't send this report to anyone. Double-check the addresses and try again." };
}

export function partitionFailedRecipients(recipients: ContactRecipient[], failedValues: string[]): ContactRecipient[] {
	const failedSet = new Set(failedValues);
	return recipients.filter((recipient) => failedSet.has(recipient.value));
}

/**
 * True when any recipient is an invalid entry (a red "invalid" pill — see ContactRecipient.valid).
 * The Share dialog uses this to keep the user from sending a report to a phone number or email
 * address that didn't pass validation (LSP-16629 / LSP-16630). `valid` undefined means valid, so
 * contact-sourced and successfully-canonicalized recipients never count as invalid.
 */
export function hasInvalidRecipients(recipients: ContactRecipient[]): boolean {
	return recipients.some((recipient) => recipient.valid === false);
}
